/*eslint max-lines-per-function: ["error", 180]*/
/*eslint complexity: ["error", 23]*/
import React from 'react';
import { Form } from 'react-bootstrap';

import { CreateUpdateContractDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import FormRow from '../../../app/components/FormRow';
import DynamicTextarea from '../../../app/components/DynamicTextarea';
import CurrencyControl from '../../../app/components/CurrencyControl';
import { useTranslation } from '../../../common/hooks/useTranslation';
import EmployeeSelector from '../Selects/EmployeeSelector';
import { ClientSelector } from '../Selects/ClientSelector';
import ProjectSelector from '../Selects/ProjectSelector';
import { handleChangeSelection } from '../Selects/selectUtils';
import { Option } from '../Selects/Selector';
import { CreatableClearableSelector } from '../Selects/CreatableClearableSelector';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { LanguageSelector } from '../Selects/LanguageSelector';

interface ContractDetailsFormProps {
  contract: CreateUpdateContractDto;
  propertyChange: (obj: Partial<CreateUpdateContractDto>) => void;
  readOnly: boolean;
  hasEstimateItemsWithAmounts: boolean;
  clientLanguage?: string;
}

export const ContractDetailsForm: React.FC<
  ContractDetailsFormProps
> = props => {
  const { t, tk } = useTranslation('projectLng');
  const pok = usePokCore();

  return (
    <>
      <FormRow
        controlId="signAttachment"
        label={t(tk.contract.signedOutsidePok)}
      >
        <Form.Check
          type="checkbox"
          className="pt-2"
          checked={!!props.contract.signedOutsidePok}
          onChange={e =>
            props.propertyChange({ signedOutsidePok: e.target.checked })
          }
          disabled={props.readOnly}
        />
      </FormRow>
      {/* <FormRow controlId="tv" label={t(tk.contract.tv)}>
        <Form.Check
          type="checkbox"
          className="pt-2"
          checked={!!props.contract.tv}
          onChange={e => props.propertyChange({ tv: e.target.checked })}
          disabled={props.readOnly || props.hasEstimateItemsWithAmounts}
        />
      </FormRow> */}
      <FormRow controlId="contractNumber" label={t(tk.common.number)}>
        <Form.Control
          type="text"
          value={props.contract?.number ?? ''}
          onChange={e =>
            props.propertyChange({
              number: e.target.value,
            })
          }
          disabled={props.readOnly}
        />
      </FormRow>
      <FormRow controlId="amount" label={t(tk.contract.amount)}>
        <CurrencyControl
          decimalScale={2}
          value={props.contract?.amount}
          onChange={value =>
            props.propertyChange({
              amount: value || undefined,
            })
          }
          disabled={props.readOnly}
        />
      </FormRow>
      <FormRow controlId="project" label={t(tk.prLang.project)}>
        <ProjectSelector
          onChange={option =>
            handleChangeSelection(
              option,
              'projectId',
              props.propertyChange,
              'clientId',
            )
          }
          value={props.contract?.projectId}
          readOnly={props.readOnly || props.hasEstimateItemsWithAmounts}
        />
      </FormRow>
      <FormRow controlId="client" label={t(tk.contract.contractor)}>
        <ClientSelector
          onChange={option =>
            handleChangeSelection(option, 'clientId', props.propertyChange)
          }
          projectId={props.contract?.projectId}
          valueId={props.contract?.clientId}
          buildName={client =>
            `${client.name} (${client?.shortname ? client?.shortname : client?.name})`
          }
          readOnly={
            !props.contract?.projectId ||
            props.readOnly ||
            props.hasEstimateItemsWithAmounts
          }
        />
      </FormRow>
      {props.clientLanguage && (
        <FormRow
          controlId="contractorLanguage"
          label={t(tk.contract.contractorLanguage)}
        >
          <LanguageSelector
            value={props.clientLanguage}
            readOnly
            withName={true}
          />
        </FormRow>
      )}
      <FormRow
        controlId="contractorEmail"
        label={t(tk.contract.contractorEmail)}
      >
        <CreatableClearableSelector
          uniqueKey={props.contract?.clientId || ''}
          value={props.contract?.contractorEmail}
          valueToShow={true}
          onChange={option =>
            props.propertyChange({
              contractorEmail: (option as Option)?.label,
            })
          }
          provider={(text: string) =>
            pok.contracts.getContractEmailsByClientId(
              text,
              props.contract?.clientId || '',
            )
          }
          readOnly={props.readOnly}
        />
      </FormRow>
      <FormRow
        controlId="comment"
        label={t(tk.contract.additionalEmailContent)}
      >
        <DynamicTextarea
          value={props.contract?.additionalEmailContent ?? ''}
          onChange={e =>
            props.propertyChange &&
            props.propertyChange({ additionalEmailContent: e.target.value })
          }
          disabled={props.readOnly}
        />
      </FormRow>
      <FormRow
        controlId="notSendAutomatically"
        label={t(tk.contract.notSendAutomatically)}
      >
        <Form.Check
          type="checkbox"
          className="pt-2"
          checked={!!props.contract.notSendAutomatically}
          onChange={e =>
            props.propertyChange({ notSendAutomatically: e.target.checked })
          }
          disabled={props.readOnly}
        />
      </FormRow>
      <FormRow
        controlId="authorizedPerson"
        label={t(tk.contract.authorizedPerson)}
      >
        <EmployeeSelector
          value={props.contract?.authorizedOwnerId ?? null}
          onChange={e =>
            props.propertyChange({ authorizedOwnerId: (e as Option)?.value })
          }
          readOnly={props.readOnly}
          fetchAuthorized={true}
          projectIdForAuthorized={props.contract?.projectId || ''}
        />
      </FormRow>
      <FormRow
        controlId="responsibleOwner"
        label={t(tk.common.responsiblePerson)}
      >
        <EmployeeSelector
          value={props.contract?.responsibleOwnerId ?? null}
          onChange={option =>
            handleChangeSelection(
              option,
              'responsibleOwnerId',
              props.propertyChange,
            )
          }
          readOnly={props.readOnly}
        />
      </FormRow>
    </>
  );
};
